// @ts-nocheck
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  EyeIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { URL } from '../../service/Service';
import { getCustomerStatusLabel } from '../../utils/customerStatus';
import ClientModal from './ClientModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import EditClientModal from './EditClientModal';

const normalizeExternalUrl = (url?: string) => {
  if (!url) return '';

  const value = url.trim();

  if (!value) return '';
  if (value.startsWith('http://') || value.startsWith('https://')) return value;

  return `https://${value}`;
};

const exportCsv = (rows, filename = 'clientes.csv') => {
  const headers = [
    'CID',
    'NIF',
    'Nome',
    'Website',
    'Estado',
    'Data Registo',
    'Último Levantamento',
    'Notas',
  ];

  const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const body = rows.map((row) =>
      [
        row.customerID,
        row.nif,
        row.name,
        row.site,
        getCustomerStatusLabel(row.customerStatus),
        row.createAt,
        row.dateCalculate,
        row.notes,
      ]
          .map(escape)
          .join(';')
  );

  const csv = [headers.join(';'), ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();

  window.URL.revokeObjectURL(url);
};

const statusClass = (status) => {
  switch (status) {
    case 'APPROVED':
      return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
    case 'PENDING':
      return 'bg-amber-50 text-amber-700 ring-amber-200';
    case 'REJECTED':
      return 'bg-red-50 text-red-700 ring-red-200';
    default:
      return 'bg-gray-50 text-gray-600 ring-gray-200';
  }
};

const getFriendlyErrorMessage = async (response) => {
  const fallback =
      'Não foi possível carregar os clientes. Tente novamente mais tarde.';

  try {
    const text = await response.text();

    if (!text) {
      return fallback;
    }

    if (
        text.toLowerCase().includes('costumer list is empty') ||
        text.toLowerCase().includes('customer list is empty')
    ) {
      return 'EMPTY_CLIENTS';
    }

    try {
      const json = JSON.parse(text);

      return json?.message || json?.error || json?.detail || fallback;
    } catch {
      return fallback;
    }
  } catch {
    return fallback;
  }
};

export const TableClients = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(10);
  const [expandedClientID, setExpandedClientID] = useState(null);
  const [editClientID, setEditClientID] = useState(null);

  const { id } = useParams();
  const jwtToken = sessionStorage.getItem('jwt');

  const getClients = useCallback(async () => {
    try {
      setError(null);
      setInfoMessage(null);

      const response = await fetch(`${URL}/api/partners/${id}/costumers/`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const message = await getFriendlyErrorMessage(response);

        setClients([]);
        setCurrentPage(1);

        if (message === 'EMPTY_CLIENTS') {
          setInfoMessage('Este parceiro ainda não tem clientes associados.');
          setError(null);
          return;
        }

        setInfoMessage(null);
        setError(message);
        return;
      }

      const text = await response.text();

      if (!text) {
        setClients([]);
        setError(null);
        setInfoMessage('Este parceiro ainda não tem clientes associados.');
        return;
      }

      const data = JSON.parse(text);

      if (!Array.isArray(data)) {
        setClients([]);
        setInfoMessage(null);
        setError('Não foi possível carregar a lista de clientes.');
        return;
      }

      const orderedClients = [...data].reverse();

      setClients(orderedClients);
      setError(null);
      setInfoMessage(
          orderedClients.length === 0
              ? 'Este parceiro ainda não tem clientes associados.'
              : null
      );
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
      setClients([]);
      setInfoMessage(null);
      setError(
          'Não foi possível carregar os clientes. Verifique a ligação e tente novamente.'
      );
    }
  }, [id, jwtToken]);

  useEffect(() => {
    if (!id || !jwtToken) return;

    getClients();
  }, [id, jwtToken, getClients]);

  const filteredClients = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    if (!q) return clients;

    return clients.filter((client) =>
        `${client.name || ''} ${client.customerID || ''} ${client.nif || ''} ${
            client.site || ''
        }`
            .toLowerCase()
            .includes(q)
    );
  }, [clients, searchTerm]);

  const totalPages = Math.max(
      1,
      Math.ceil(filteredClients.length / postsPerPage)
  );

  const currentClients =
      postsPerPage === 9999
          ? filteredClients
          : filteredClients.slice(
              (currentPage - 1) * postsPerPage,
              currentPage * postsPerPage
          );

  const handleDelete = async (customerID) => {
    try {
      await axios.delete(`${URL}/api/partners/${id}/customer/${customerID}`, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      });

      setClients((prev) =>
          prev.filter((client) => client.customerID !== customerID)
      );

      setError(null);
    } catch (err) {
      console.error('Erro ao apagar cliente:', err);
      setError('Não foi possível apagar o cliente. Tente novamente.');
    }
  };

  return (
      <div className="min-w-0 overflow-hidden rounded-3xl bg-white p-6 shadow-xl ring-1 ring-gray-100">
        <div className="flex flex-col gap-4 border-b border-gray-100 pb-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-green-cl">
              Clientes do parceiro
            </p>

            <h2 className="mt-1 text-2xl font-black text-gray-950">
              Gestão de clientes
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Pesquise por nome, CID, NIF ou website. Abra detalhes sem sair da
              página ou exporte a lista para CSV.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
                onClick={() => exportCsv(filteredClients, `clientes-parceiro-${id}.csv`)}
                className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-gray-200 bg-white px-5 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Exportar CSV
            </button>

            <ClientModal />
          </div>
        </div>

        {infoMessage && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-800">
              {infoMessage}
            </div>
        )}

        {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
              {error}
            </div>
        )}

        <div className="mt-5 grid gap-3 md:grid-cols-[1fr_190px]">
          <input
              type="search"
              placeholder="Pesquisar por nome, CID, NIF ou website"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
              className="h-14 w-full rounded-2xl border border-gray-300 bg-white px-5 text-base text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-green-cl focus:ring-2 focus:ring-green-cl/20"
          />

          <div className="relative h-14 min-w-[190px]">
            <select
                value={postsPerPage}
                onChange={(event) => {
                  setPostsPerPage(Number(event.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                  backgroundImage: 'none',
                }}
                className="block h-14 w-full appearance-none rounded-2xl border-2 border-green-cl bg-white pl-5 pr-12 text-center text-sm font-bold leading-normal text-gray-950 shadow-sm outline-none transition focus:border-green-cl focus:ring-2 focus:ring-green-cl/20"
            >
              <option value={10}>10 por página</option>
              <option value={25}>25 por página</option>
              <option value={50}>50 por página</option>
              <option value={9999}>Ver todos</option>
            </select>

            <ChevronDownIcon
                className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-green-cl"
                aria-hidden="true"
            />
          </div>
        </div>

        <div className="mt-5 overflow-x-auto rounded-2xl border border-gray-100">
          <table className="min-w-[900px] divide-y divide-gray-100">
            <thead className="bg-green-cl text-white">
            <tr>
              <th title="Identificador do cliente" style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                ID de Cliente (CID)
              </th>
              <th title="Número de Identificação Fiscal" style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                NIF
              </th>
              <th style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                Cliente
              </th>
              <th style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                Website
              </th>
              <th style={{ color: '#ffffff' }} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wide">
                Estado
              </th>
              <th style={{ color: '#ffffff' }} className="px-5 py-4 text-right text-xs font-bold uppercase tracking-wide">
                Ações
              </th>
            </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 bg-white">
            {currentClients.length === 0 ? (
                <tr>
                  <td
                      colSpan={6}
                      className="px-5 py-10 text-center text-sm text-gray-500"
                  >
                    Nenhum cliente encontrado.
                  </td>
                </tr>
            ) : (
                currentClients.map((client) => (
                    <React.Fragment key={client.customerID}>
                      <tr className="transition hover:bg-gray-50">
                        <td className="px-5 py-4 text-sm font-bold text-gray-950">
                          {client.customerID}
                        </td>

                        <td className="px-5 py-4 text-sm text-gray-700">
                          {client.nif || '-'}
                        </td>

                        <td className="px-5 py-4 text-sm text-gray-900">
                          {client.name}
                        </td>

                        <td className="px-5 py-4 text-sm">
                          {client.site ? (
                              <a
                                  className="font-semibold text-green-cl hover:underline"
                                  href={normalizeExternalUrl(client.site)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                              >
                                {client.site}
                              </a>
                          ) : (
                              '-'
                          )}
                        </td>

                        <td className="px-5 py-4">
                      <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1 ${statusClass(
                              client.customerStatus
                          )}`}
                      >
                        {getCustomerStatusLabel(client.customerStatus)}
                      </span>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <div className="inline-flex items-center gap-2">
                            <button
                                onClick={() =>
                                    setExpandedClientID(
                                        expandedClientID === client.customerID
                                            ? null
                                            : client.customerID
                                    )
                                }
                                className="portal-icon-action portal-icon-action-view"
                                aria-label="Ver detalhes"
                                title="Ver detalhes"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>

                            <button
                                onClick={() => setEditClientID(client.customerID)}
                                className="portal-icon-action portal-icon-action-edit"
                                aria-label="Editar cliente"
                                title="Editar"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>

                            <ConfirmDeleteModal
                                customerID={client.customerID}
                                handleDelete={handleDelete}
                            />
                          </div>
                        </td>
                      </tr>

                      {expandedClientID === client.customerID && (
                          <tr className="bg-gray-50/70">
                            <td colSpan={6} className="px-5 py-5">
                              <div className="grid gap-4 rounded-2xl bg-white p-5 text-sm ring-1 ring-gray-100 md:grid-cols-3">
                                <div>
                                  <p className="text-xs font-bold uppercase text-gray-400">
                                    Data registo
                                  </p>
                                  <p className="mt-1 text-gray-900">
                                    {client.createAt || '-'}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-xs font-bold uppercase text-gray-400">
                                    Último levantamento
                                  </p>
                                  <p className="mt-1 text-gray-900">
                                    {client.dateCalculate || '-'}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-xs font-bold uppercase text-gray-400">
                                    Estado de alteração
                                  </p>
                                  <p className="mt-1 text-gray-900">
                                    {getCustomerStatusLabel(
                                        client.customerStatusUpdate,
                                        '-'
                                    )}
                                  </p>
                                </div>

                                <div className="md:col-span-3">
                                  <p className="text-xs font-bold uppercase text-gray-400">
                                    Notas
                                  </p>
                                  <p className="mt-1 text-gray-900">
                                    {client.notes || '-'}
                                  </p>
                                </div>
                              </div>
                            </td>
                          </tr>
                      )}
                    </React.Fragment>
                ))
            )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-col gap-3 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
          <span>{filteredClients.length} cliente(s)</span>

          {postsPerPage !== 9999 && (
              <div className="flex items-center gap-2">
                <button
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((page) => page - 1)}
                    className="rounded-lg border px-3 py-1 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Anterior
                </button>

                <span className="px-2 py-1 font-semibold text-gray-700">
              {currentPage}/{totalPages}
            </span>

                <button
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((page) => page + 1)}
                    className="rounded-lg border px-3 py-1 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Seguinte
                </button>
              </div>
          )}
        </div>

        {editClientID && (
            <EditClientModal
                customerID={editClientID}
                onClose={() => setEditClientID(null)}
            />
        )}
      </div>
  );
};
